/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as cheerio from 'cheerio';

import { IDefaProductDetailsData } from './defa-product-details.data';
import { IDefaProductDetailsProps } from './defa-product-details.props.autogenerated';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import DefaProductDetailsSection from './components/section';
import { AttributeDataType, AttributeValue, MediaType } from '@msdyn365-commerce/retail-proxy';
import TabComponent, { TabPage } from './components/tab-component';

export interface IDefaProductDetailsViewProps extends IDefaProductDetailsProps<IDefaProductDetailsData> {
    baseClassName: string;
    ModuleProps: IModuleProps;
    sections: JSX.Element | JSX.Element[];
}

export interface IDefaProductDetailsState {
    sectionStates: { [key: string]: boolean };
}

/**
 *
 * DefaProductDetails component
 * @extends {React.PureComponent<IDefaProductDetailsProps<IDefaProductDetailsData>>}
 */
class DefaProductDetails extends React.PureComponent<IDefaProductDetailsProps<IDefaProductDetailsData>, IDefaProductDetailsState> {
    private baseClassName: string = 'defa-product-details';

    constructor(props: IDefaProductDetailsProps<IDefaProductDetailsData>) {
        super(props);
        this.state = {
            sectionStates: {
                description: true,
                specifications: true,
                downloads: true
            }
        };
    }

    public render(): JSX.Element | null {
        const viewProps: IDefaProductDetailsViewProps = {
            ...this.props,
            ModuleProps: {
                moduleProps: this.props,
                className: this.baseClassName
            },
            baseClassName: this.baseClassName,
            sections: this.getSectionsTabbed()
        };
        return this.props.renderView(viewProps);
    }

    private isHtmlEmpty(data: string): boolean {
        // Parses HTML and makes sure the visible text is actually empty.
        const $ = cheerio.load(data);
        const text = $.root().text();
        return text
            .replace(/\u00a0/g, ' ')            // NBSP → space
            .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-widths
            .trim().length === 0;
    }

    // @ts-expect-error
    private getSections(): JSX.Element[] {
        const {
            data: { additionalMediaLocations, productSpecificationData },
            resources
        } = this.props;
        const sections: JSX.Element[] = [];

        if (this.props.config.description && !this.isHtmlEmpty(this.props.config.description.toString())) {
            sections.push(
                <DefaProductDetailsSection id='description' baseClassName={this.baseClassName} title={resources.descriptionSectionHeading}>
                    <div
                        className={`${this.baseClassName}__description`}
                        dangerouslySetInnerHTML={{ __html: this.props.config.description as string }}
                    />
                </DefaProductDetailsSection>
            );
        }

        if (productSpecificationData.result && productSpecificationData.result.length > 0) {
            sections.push(
                <DefaProductDetailsSection id='assembly' baseClassName={this.baseClassName} title={resources.specificationSectionHeading}>
                    <ul className={`${this.baseClassName}__specifications__list`}>
                        {productSpecificationData.result.map((spec, index) => {
                            const value = this.getAttributeValue(spec);

                            if (!value) {
                                return null; // Skip rendering if value is undefined or empty
                            }

                            return (
                                <li key={index} className={`${this.baseClassName}__specification`}>
                                    <strong>{spec.Name}:</strong> {value}
                                </li>
                            );
                        })}
                    </ul>
                </DefaProductDetailsSection>
            );
        }

        // Sort downloads by DisplayOrder
        const downloads = additionalMediaLocations.result?.filter(x => x.MediaTypeValue === MediaType.File);

        downloads?.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

        if (downloads && downloads.length > 0) {
            sections.push(
                <DefaProductDetailsSection id='downloads' baseClassName={this.baseClassName} title={resources.downloadsSectionHeading}>
                    <ul className={`${this.baseClassName}__downloads__list`}>
                        {downloads.map((download, index) => (
                            <li key={index} className={`${this.baseClassName}__download`}>
                                <a href={download.Uri} target='_blank' rel='noopener noreferrer'>
                                    {download.AltText}
                                </a>
                            </li>
                        ))}
                    </ul>
                </DefaProductDetailsSection>
            );
        }

        return sections;
    }

    private getSectionsTabbed(): JSX.Element {
        const {
            data: { additionalMediaLocations, productSpecificationData },
            resources
        } = this.props;
        const sections: React.ReactElement<typeof TabPage>[] = [];

        if (this.props.config.description && !this.isHtmlEmpty(this.props.config.description.toString())) {
            sections.push(
                <TabPage id='description' baseClassName={this.baseClassName} title={resources.descriptionSectionHeading}>
                    <div
                        className={`${this.baseClassName}__description`}
                        dangerouslySetInnerHTML={{ __html: this.props.config.description as string }}
                    />
                </TabPage>
            );
        }

        if (productSpecificationData.result && productSpecificationData.result.length > 0) {
            sections.push(
                <TabPage id='assembly' baseClassName={this.baseClassName} title={resources.specificationSectionHeading}>
                    <ul className={`${this.baseClassName}__specifications__list`}>
                        {productSpecificationData.result.map((spec, index) => {
                            const value = this.getAttributeValue(spec);

                            if (!value) {
                                return null; // Skip rendering if value is undefined or empty
                            }

                            return (
                                <li key={index} className={`${this.baseClassName}__specification`}>
                                    <strong>{spec.Name}:</strong> {value}
                                </li>
                            );
                        })}
                    </ul>
                </TabPage>
            );
        }

        // Sort downloads by DisplayOrder
        const downloads = additionalMediaLocations.result?.filter(x => x.MediaTypeValue === MediaType.File);

        downloads?.sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));

        if (downloads && downloads.length > 0) {
            sections.push(
                <TabPage id='downloads' baseClassName={this.baseClassName} title={resources.downloadsSectionHeading}>
                    <ul className={`${this.baseClassName}__downloads__list`}>
                        {downloads.map((download, index) => (
                            <li key={index} className={`${this.baseClassName}__download`}>
                                <a href={download.Uri} target='_blank' rel='noopener noreferrer'>
                                    {download.AltText}
                                </a>
                            </li>
                        ))}
                    </ul>
                </TabPage>
            );
        }

        return (
            <TabComponent baseClassName={this.baseClassName}>
                {sections}
            </TabComponent>
        );
    }

    private getAttributeValue(attribute: AttributeValue): JSX.Element | undefined {
        switch (attribute.DataTypeValue) {
            case AttributeDataType.Currency:
                return attribute.CurrencyValue ? <span>{attribute.CurrencyValue.toString()}</span> : undefined;
            case AttributeDataType.DateTime:
                return attribute.DateTimeOffsetValue ? <span>{attribute.DateTimeOffsetValue.toISOString()}</span> : undefined;
            case AttributeDataType.Decimal:
                return attribute.FloatValue ? <span>{attribute.FloatValue.toString()}</span> : undefined;
            case AttributeDataType.Integer:
                return attribute.IntegerValue ? <span>{attribute.IntegerValue.toString()}</span> : undefined;
            case AttributeDataType.Text:
                return attribute.TextValue ? (
                    <span dangerouslySetInnerHTML={Msdyn365.getRichTextHtml(this.convertTextToHtml(attribute.TextValue))} />
                ) : (
                    undefined
                );
            case AttributeDataType.TrueFalse:
                return attribute.BooleanValue !== undefined ? <span>{attribute.BooleanValue ? 'true' : 'false'}</span> : undefined;
            default:
                return undefined;
        }
    }

    private convertTextToHtml(text: string): string {
        return text
            .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
            .replace(/\n/g, '<br/>')
            .trim();
    }
}

export default DefaProductDetails;
